// Node graph for an act. 8 layers (index 0..7). Currently the player is "on"
// a node once selected; completing it unlocks nodes in the next layer
// connected by edges.

import type { NodeType } from "../data/nodes";

export interface MapNode {
  id: string;
  type: NodeType;
  layer: number; // 0..7
  indexInLayer: number; // 0..(layerWidth-1)
  // Normalized position in [0,1] across the map board. The renderer maps these
  // to pixel coords based on current board size.
  x: number;
  y: number;
  // Set for jungleCamp nodes to show which monster the player fights.
  campType?: string;
}

export interface MapEdge {
  from: string;
  to: string;
}

export class CampaignMap {
  private outgoing = new Map<string, string[]>();
  private incoming = new Map<string, string[]>();

  constructor(
    public readonly nodes: MapNode[],
    public readonly edges: MapEdge[],
    public currentNodeId: string | null,
    public completedNodeIds = new Set<string>(),
  ) {
    for (const n of nodes) {
      this.outgoing.set(n.id, []);
      this.incoming.set(n.id, []);
    }
    for (const e of edges) {
      this.outgoing.get(e.from)?.push(e.to);
      this.incoming.get(e.to)?.push(e.from);
    }
  }

  getNode(id: string): MapNode | undefined {
    return this.nodes.find((n) => n.id === id);
  }

  nodesByLayer(layer: number): MapNode[] {
    return this.nodes.filter((n) => n.layer === layer);
  }

  // Nodes the player can click right now.
  availableNodes(): MapNode[] {
    if (!this.currentNodeId) {
      // No node chosen yet — the starting layer (layer 0) is open.
      return this.nodesByLayer(0);
    }
    const cur = this.getNode(this.currentNodeId);
    if (!cur) return [];
    if (!this.completedNodeIds.has(cur.id)) return [cur];
    const nextIds = this.outgoing.get(cur.id) ?? [];
    return nextIds.map((id) => this.getNode(id)!).filter(Boolean);
  }

  outgoingFor(nodeId: string): string[] {
    return this.outgoing.get(nodeId) ?? [];
  }

  markComplete(nodeId: string): void {
    this.completedNodeIds.add(nodeId);
  }

  advance(nodeId: string): void {
    this.currentNodeId = nodeId;
  }

  isActComplete(): boolean {
    if (!this.currentNodeId) return false;
    const cur = this.getNode(this.currentNodeId);
    if (!cur) return false;
    if (!this.completedNodeIds.has(cur.id)) return false;
    return (this.outgoing.get(cur.id) ?? []).length === 0;
  }
}
